import { supabase } from '../lib/supabase';

/**
 * Learning Trails Service for managing learning trails and trail content
 */

/**
 * Create a new learning trail
 * @param {Object} trailData - Trail data
 * @returns {Promise<{trail: Object, error: null} | {trail: null, error: string}>}
 */
export const createLearningTrail = async (trailData) => {
  try {
    // Always use auth.uid() for teacher_id to enforce ownership
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return { trail: null, error: 'User not authenticated' };
    }

    const { data, error } = await supabase?.from('learning_trails')?.insert([{
        teacher_id: user?.id,
        classroom_id: trailData?.classroomIds?.[0] || trailData?.classroomId || null,
        title: trailData?.title,
        description: trailData?.description,
        image_url: trailData?.imageUrl,
        qr_code_url: trailData?.qrCodeUrl,
        published: trailData?.published || false,
        min_grade_level: trailData?.minGradeLevel || 'All Grades'
      }])?.select()?.single();

    if (error) {
      console.error('Create trail error:', error);
      return { trail: null, error: error?.message };
    }

    // Insert multiple classroom assignments into junction table
    const classroomIds = trailData?.classroomIds || (trailData?.classroomId ? [trailData?.classroomId] : []);
    if (classroomIds?.length > 0) {
      const junctionRows = classroomIds?.map(cid => ({ trail_id: data?.id, classroom_id: cid }));
      const { error: junctionError } = await supabase?.from('trail_classrooms')?.insert(junctionRows);
      if (junctionError) {
        console.error('Trail classrooms insert error:', junctionError);
      }
    }

    return { trail: data, error: null };
  } catch (error) {
    console.error('Create trail exception:', error);
    return { trail: null, error: 'Failed to create learning trail' };
  }
};

/**
 * Get all learning trails for a teacher
 * @param {string} teacherId - The teacher's ID (optional, falls back to auth.uid())
 * @returns {Promise<{trails: Array, error: null} | {trails: null, error: string}>}
 */
export const getTeacherTrails = async (teacherId) => {
  try {
    // Always resolve to auth.uid() to enforce data isolation
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      return { trails: [], error: 'User not authenticated' };
    }
    const resolvedTeacherId = user?.id;

    const { data, error } = await supabase?.from('learning_trails')?.select(`
        *,
        trail_classrooms (
          classroom_id,
          classrooms ( classroom_name, grade_level )
        )
      `)?.eq('teacher_id', resolvedTeacherId)?.order('created_at', { ascending: false });

    if (error) {
      console.error('Get trails error:', error);
      return { trails: null, error: error?.message };
    }

    return { trails: data, error: null };
  } catch (error) {
    console.error('Get trails exception:', error);
    return { trails: null, error: 'Failed to fetch learning trails' };
  }
};

/**
 * Get a single learning trail by ID
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{trail: Object, error: null} | {trail: null, error: string}>}
 */
export const getTrailById = async (trailId) => {
  try {
    const { data, error } = await supabase?.from('learning_trails')?.select(`
        *,
        trail_classrooms (
          classroom_id,
          classrooms ( classroom_name, grade_level )
        )
      `)?.eq('id', trailId)?.single();

    if (error) {
      console.error('Get trail error:', error);
      return { trail: null, error: error?.message };
    }

    return { trail: data, error: null };
  } catch (error) {
    console.error('Get trail exception:', error);
    return { trail: null, error: 'Failed to fetch learning trail' };
  }
};

/**
 * Update a learning trail
 * @param {string} trailId - The trail's ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{trail: Object, error: null} | {trail: null, error: string}>}
 */
export const updateLearningTrail = async (trailId, updates) => {
  try {
    const updateData = {};
    if (updates?.title !== undefined) updateData.title = updates?.title;
    if (updates?.description !== undefined) updateData.description = updates?.description;
    if (updates?.imageUrl !== undefined) updateData.image_url = updates?.imageUrl;
    if (updates?.qrCodeUrl !== undefined) updateData.qr_code_url = updates?.qrCodeUrl;
    if (updates?.published !== undefined) updateData.published = updates?.published;
    if (updates?.classroomId !== undefined) updateData.classroom_id = updates?.classroomId;
    if (updates?.classroomIds !== undefined) updateData.classroom_id = updates?.classroomIds?.[0] || null;
    if (updates?.minGradeLevel !== undefined) updateData.min_grade_level = updates?.minGradeLevel;

    const { data, error } = await supabase?.from('learning_trails')?.update(updateData)?.eq('id', trailId)?.select()?.single();

    if (error) {
      console.error('Update trail error:', error);
      return { trail: null, error: error?.message };
    }

    // Update junction table if classroomIds provided
    if (updates?.classroomIds !== undefined) {
      // Delete existing assignments
      await supabase?.from('trail_classrooms')?.delete()?.eq('trail_id', trailId);
      // Insert new assignments
      if (updates?.classroomIds?.length > 0) {
        const junctionRows = updates?.classroomIds?.map(cid => ({ trail_id: trailId, classroom_id: cid }));
        const { error: junctionError } = await supabase?.from('trail_classrooms')?.insert(junctionRows);
        if (junctionError) {
          console.error('Trail classrooms update error:', junctionError);
        }
      }
    }

    return { trail: data, error: null };
  } catch (error) {
    console.error('Update trail exception:', error);
    return { trail: null, error: 'Failed to update learning trail' };
  }
};

/**
 * Delete a learning trail
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{success: boolean, error: null} | {success: false, error: string}>}
 */
export const deleteLearningTrail = async (trailId) => {
  try {
    const { error } = await supabase?.from('learning_trails')?.delete()?.eq('id', trailId);

    if (error) {
      console.error('Delete trail error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete trail exception:', error);
    return { success: false, error: 'Failed to delete learning trail' };
  }
};

/**
 * Create trail content
 * @param {Object} contentData - Content data
 * @returns {Promise<{content: Object, error: null} | {content: null, error: string}>}
 */
export const createTrailContent = async (contentData) => {
  try {
    const { data, error } = await supabase?.from('trail_content')?.insert([{
        trail_id: contentData?.trailId,
        content_type: contentData?.contentType,
        content_data: contentData?.contentData,
        order_position: contentData?.orderPosition,
        page_number: contentData?.pageNumber || 1
      }])?.select()?.single();

    if (error) {
      console.error('Create content error:', error);
      return { content: null, error: error?.message };
    }

    return { content: data, error: null };
  } catch (error) {
    console.error('Create content exception:', error);
    return { content: null, error: 'Failed to create trail content' };
  }
};

/**
 * Get all content for a trail
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{contents: Array, error: null} | {contents: null, error: string}>}
 */
export const getTrailContent = async (trailId) => {
  try {
    const { data, error } = await supabase?.from('trail_content')?.select('*')?.eq('trail_id', trailId)?.order('order_position', { ascending: true });

    if (error) {
      console.error('Get content error:', error);
      return { contents: null, error: error?.message };
    }

    return { contents: data, error: null };
  } catch (error) {
    console.error('Get content exception:', error);
    return { contents: null, error: 'Failed to fetch trail content' };
  }
};

/**
 * Update trail content
 * @param {string} contentId - The content's ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{content: Object, error: null} | {content: null, error: string}>}
 */
export const updateTrailContent = async (contentId, updates) => {
  try {
    const updateData = {};
    if (updates?.contentType !== undefined) updateData.content_type = updates?.contentType;
    if (updates?.contentData !== undefined) updateData.content_data = updates?.contentData;
    if (updates?.orderPosition !== undefined) updateData.order_position = updates?.orderPosition;
    if (updates?.pageNumber !== undefined) updateData.page_number = updates?.pageNumber;

    const { data, error } = await supabase?.from('trail_content')?.update(updateData)?.eq('id', contentId)?.select()?.single();

    if (error) {
      console.error('Update content error:', error);
      return { content: null, error: error?.message };
    }

    return { content: data, error: null };
  } catch (error) {
    console.error('Update content exception:', error);
    return { content: null, error: 'Failed to update trail content' };
  }
};

/**
 * Delete trail content
 * @param {string} contentId - The content's ID
 * @returns {Promise<{success: boolean, error: null} | {success: false, error: string}>}
 */
export const deleteTrailContent = async (contentId) => {
  try {
    const { error } = await supabase?.from('trail_content')?.delete()?.eq('id', contentId);

    if (error) {
      console.error('Delete content error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete content exception:', error);
    return { success: false, error: 'Failed to delete trail content' };
  }
};

/**
 * Delete all content for a trail
 * @param {string} trailId - The trail's ID
 * @returns {Promise<{success: boolean, error: null} | {success: false, error: string}>}
 */
export const deleteAllTrailContent = async (trailId) => {
  try {
    const { error } = await supabase?.from('trail_content')?.delete()?.eq('trail_id', trailId);

    if (error) {
      console.error('Delete all content error:', error);
      return { success: false, error: error?.message };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Delete all content exception:', error);
    return { success: false, error: 'Failed to delete all trail content' };
  }
};

/**
 * Create multiple trail content items in batch
 * @param {string} trailId - The trail's ID
 * @param {Array} contentBlocks - Array of content blocks
 * @returns {Promise<{contents: Array, error: null} | {contents: null, error: string}>}
 */
export const createTrailContentBatch = async (trailId, contentBlocks) => {
  try {
    const contentData = contentBlocks?.map((block, index) => {
      // Transform content block to proper format for database
      let contentDataObj = {};
      
      // Handle different content types and their data structures
      switch (block?.type) {
        case 'text':
          contentDataObj = {
            title: block?.title || 'Text Section',
            body: block?.content || ''
          };
          break;
        case 'image':
          contentDataObj = {
            url: block?.url || '',
            caption: block?.caption || '',
            alt: block?.alt || 'Educational image'
          };
          break;
        case 'video':
          contentDataObj = {
            url: block?.url || '',
            title: block?.title || 'Video',
            duration: block?.duration || 0
          };
          break;
        case 'quiz':
          contentDataObj = {
            question: block?.question || '',
            options: block?.options || [],
            correct_answer: parseInt(block?.correctAnswer) || 0
          };
          break;
        case 'game':
          contentDataObj = {
            gameType: block?.gameType || 'matching',
            title: block?.title || 'Interactive Game',
            description: block?.description || '',
            gameData: {
                pairs: block?.pairs || block?.gameData?.pairs || [],
                words: block?.words || block?.gameData?.words || [],
                gridSize: block?.gridSize || block?.gameData?.gridSize || '10x10',
                customWidth: block?.customWidth || block?.gameData?.customWidth || '',
                customHeight: block?.customHeight || block?.gameData?.customHeight || '',
                items: block?.items || block?.gameData?.items || [],
                statement: block?.statement || block?.gameData?.statement || '',
                answer: block?.answer || block?.gameData?.answer || '',
                blanks: block?.blanks || block?.gameData?.blanks || [],
                categories: block?.categories || block?.gameData?.categories || [],
                hotspotImage: block?.hotspotImage || block?.gameData?.hotspotImage || '',
                hotspots: block?.hotspots || block?.gameData?.hotspots || []
              }
          };
          break;
        case 'completion':
          contentDataObj = {
            title: block?.title || 'Congratulations!',
            message: block?.message || 'You have completed this learning trail!',
            showCertificate: block?.showCertificate || false
          };
          break;
        default:
          contentDataObj = block?.data || {};
      }

      return {
        trail_id: trailId,
        content_type: block?.type,
        content_data: contentDataObj,
        order_position: block?.orderPosition !== undefined ? block?.orderPosition : index,
        page_number: block?.pageNumber || 1
      };
    });

    // Insert in chunks of 5 to avoid statement timeout
    const CHUNK_SIZE = 5;
    const allInserted = [];
    for (let i = 0; i < contentData?.length; i += CHUNK_SIZE) {
      const chunk = contentData?.slice(i, i + CHUNK_SIZE);
      const { data, error } = await supabase?.from('trail_content')?.insert(chunk)?.select();
      if (error) {
        console.error('Create batch content error:', error);
        return { contents: null, error: error?.message };
      }
      if (data) allInserted?.push(...data);
    }

    return { contents: allInserted, error: null };
  } catch (error) {
    console.error('Create batch content exception:', error);
    return { contents: null, error: 'Failed to create trail content batch' };
  }
};

/**
 * Update content order positions in batch
 * @param {Array} contentUpdates - Array of {id, orderPosition, pageNumber} objects
 * @returns {Promise<{success: boolean, error: null} | {success: false, error: string}>}
 */
export const updateContentOrderBatch = async (contentUpdates) => {
  try {
    // Update each content item's order_position and page_number
    const updatePromises = contentUpdates?.map(({ id, orderPosition, pageNumber }) => {
      const updates = {};
      if (orderPosition !== undefined) updates.order_position = orderPosition;
      if (pageNumber !== undefined) updates.page_number = pageNumber;
      return supabase?.from('trail_content')?.update(updates)?.eq('id', id);
    });

    const results = await Promise.all(updatePromises);
    
    // Check if any updates failed
    const errors = results?.filter(result => result?.error);
    if (errors?.length > 0) {
      console.error('Update order batch errors:', errors);
      return { success: false, error: 'Failed to update some content positions' };
    }

    return { success: true, error: null };
  } catch (error) {
    console.error('Update order batch exception:', error);
    return { success: false, error: 'Failed to update content order' };
  }
};